"use client";
import React, {
  useState,
  useMemo,
  useEffect,
  useCallback,
  useRef,
} from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";

import { useAuth } from "@/hooks/use-auth";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Clock,
  Users,
  Search,
  Facebook,
  Instagram,
  Twitter,
  Star,
  CalendarPlus,
  Calendar,
  CheckCircle,
  MapPin,
  Briefcase,
  TrendingUp,
  TrendingDown,
  Filter,
  Grid3X3,
  List,
  SortAsc,
  Eye,
  Heart,
  Share2,
  Share,
  Timer,
  X,
  Hourglass,
  Gavel,
  CircleStop,
  PersonStanding,
  Currency,
  AlertCircle,
  PackageSearch,
} from "lucide-react";
import Image from "next/image";
import Link from "next/link";
import { useSearchParams } from "next/navigation";
import { SubCategoryList } from "@/components/category/SubCategoryList";
import { AuctionCard, AuctionItem } from "@/components/ui/AuctionCard";
import { CategoryBannerCarousel } from "@/components/category/CategoryBannerCarousel";

const categories = [{ value: "all", label: "Category" }];

const subtypes = [
  { value: "all", label: "Auction Style" },
  { value: "sealed", label: "Sealed" },
  { value: "silent", label: "Silent" },
  { value: "dutch", label: "Dutch" },
  { value: "english", label: "English" },
];


export default function CategoryPage({
  params,
}: {
  params: Promise<{ category: string }>;
}) {
  // Unwrap the params Promise (Next.js 15+)
  const { category: categorySlug } = React.use(params);
  const { selectedMode, user } = useAuth();

  // Use the URL slug directly as the database category handle
  // The URL should match the 'handle' column in your categories table
  const dbCategoryHandle = categorySlug || "all";

  const isLoggedIn = !!user;
  const [domainMode, setDomainMode] = useState<
    "forward" | "reverse" | "marketplace"
  >("marketplace");
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedCategory, setSelectedCategory] = useState(dbCategoryHandle);
  const [isLoading, setIsLoading] = useState(true);
  const effectiveMode =
    domainMode !== "marketplace"
      ? domainMode
      : isLoggedIn
        ? selectedMode
        : "marketplace";

  // Ensure selectedCategory updates if params change
  useEffect(() => {
    if (dbCategoryHandle) {
      setSelectedCategory(dbCategoryHandle);
    }
  }, [dbCategoryHandle]);

  useEffect(() => {
    if (typeof window === "undefined") return;

    const host = window.location.hostname;
    const search = window.location.search;

    if (
      host === "forwardauction.briskon.com" ||
      host === "www.forwardauction.briskon.com" ||
      host.includes("forwardauction") ||
      search.includes("mode=forward")
    ) {
      setDomainMode("forward");
      return;
    }

    if (
      host === "reverseauction.briskon.com" ||
      host === "www.reverseauction.briskon.com" ||
      host.includes("reverseauction") ||
      search.includes("mode=reverse")
    ) {
      setDomainMode("reverse");
      return;
    }

    setDomainMode("marketplace");
  }, []);

  const [selectedLocation, setSelectedLocation] = useState("all");

  const [locations, setLocations] = useState([
    { value: "all", label: "Locations" },
  ]);
  const [selectedauctiontype, setSelectedauctiontype] = useState("all");
  const [sortBy, setSortBy] = useState("ending-soon");
  const [viewMode, setViewMode] = useState<"grid" | "list">("grid");
  const [showFilters, setShowFilters] = useState(false);
  const [selectedSubtype, setSelectedSubtype] = useState("all");
  const [allAuctionItems, setAllAuctionItems] = useState<AuctionItem[]>([]);
  const [auctionStyleSearch, setAuctionStyleSearch] = useState("");
  const [visibleLive, setVisibleLive] = useState(8);
  const [visibleUpcoming, setVisibleUpcoming] = useState(8);
  const [visibleClosed, setVisibleClosed] = useState(8);
  const [tab, setTab] = useState<"live" | "upcoming" | "closed">("live");
  const upcomingTabRef = useRef<HTMLButtonElement>(null);
  const [dbCategories, setDbCategories] = useState<
    { value: string; label: string }[]
  >([]);
  const searchParams = useSearchParams();
  const liveAuctionsCount = allAuctionItems.filter(
    (item) => item.status === "live",
  );
  // Constants derived from route
  const customHide = true;
  const excludeId = undefined;
  const heading = categorySlug
    ? categorySlug.charAt(0).toUpperCase() +
      categorySlug.slice(1).replace(/-/g, " ") +
      " Auctions"
    : "Auctions";

  useEffect(() => {
    const urlTab = searchParams.get("tab");
    const urlType = searchParams.get("type");

    if (urlTab === "live" || urlTab === "upcoming" || urlTab === "closed") {
      setTab(urlTab);
    }
    // We do NOT set category from searchParams here, as it is controlled by the route param
    if (
      (effectiveMode === "marketplace" || !effectiveMode) &&
      (urlType === "forward" || urlType === "reverse")
    ) {
      setSelectedauctiontype(urlType);
    }
  }, [searchParams, effectiveMode]);

  useEffect(() => {
    if (effectiveMode === "forward" || effectiveMode === "reverse") {
      setSelectedauctiontype(effectiveMode);
    }
  }, [effectiveMode]);
  const categoryCounts: Record<string, number> = liveAuctionsCount.reduce(
    (acc, item) => {
      const category = item.category || "uncategorized"; // adjust this key if needed
      acc[category] = (acc[category] || 0) + 1;
      return acc;
    },
    {} as Record<string, number>,
  );

  const mergedCategories = [
    ...categories,
    ...dbCategories
      .filter((dbCat) => !categories.some((cat) => cat.value === dbCat.value))
      .map((cat) => ({
        ...cat,
        label: `${cat.label} (${categoryCounts[cat.value] || 0})`, // append count
      })),
  ];

  const liveTypeCounts = liveAuctionsCount.reduce(
    (acc, item) => {
      if (item.auctiontype === "forward") acc.forward += 1;
      else if (item.auctiontype === "reverse") acc.reverse += 1;
      return acc;
    },
    { forward: 0, reverse: 0 },
  );
  const auctiontypes = useMemo(() => {
    const base = [{ value: "all", label: "Auction Type" }];
    if (effectiveMode === "forward") {
      return [
        ...base,
        {
          value: "forward",
          label: `Forward Auctions (${liveTypeCounts.forward})`,
        },
      ];
    }
    if (effectiveMode === "reverse") {
      return [
        ...base,
        {
          value: "reverse",
          label: `Reverse Auctions (${liveTypeCounts.reverse})`,
        },
      ];
    }
    return [
      ...base,
      {
        value: "forward",
        label: `Forward Auctions (${liveTypeCounts.forward})`,
      },
      {
        value: "reverse",
        label: `Reverse Auctions (${liveTypeCounts.reverse})`,
      },
    ];
  }, [effectiveMode, liveTypeCounts]);

  const locationCounts = allAuctionItems.reduce<Record<string, number>>(
    (acc, item) => {
      if (item.status === "live" && item.location?.trim()) {
        acc[item.location] = (acc[item.location] || 0) + 1;
      }
      return acc;
    },
    {},
  );
  const locationsCount = [
    { value: "all", label: "Location" },
    ...Object.entries(locationCounts).map(([loc, count]) => ({
      value: loc,
      label: `${loc} (${count})`,
    })),
  ];

  const liveSubtypeCounts: Record<string, number> = allAuctionItems
    .filter((item) => item.status === "live")
    .reduce(
      (acc, item) => {
        const subtype = item.auctionsubtype || "unknown";
        acc[subtype] = (acc[subtype] || 0) + 1;
        return acc;
      },
      {} as Record<string, number>,
    );
  const subtypesWithCounts = subtypes.map((sub) => ({
    ...sub,
    label:
      sub.value === "all"
        ? sub.label
        : `${sub.label} (${liveSubtypeCounts[sub.value] || 0})`,
  }));

  useEffect(() => {
    if (allAuctionItems.length === 0) return;

    const uniqueCategoryIds = Array.from(
      new Set(allAuctionItems.map((item) => item.category).filter(Boolean)),
    );

    const dbCats = uniqueCategoryIds.map((catId) => ({
      value: catId,
      label: catId.charAt(0).toUpperCase() + catId.slice(1).replace(/-/g, " "),
    }));

    setDbCategories([{ value: "all", label: "All Categories" }, ...dbCats]);
  }, [allAuctionItems]);
  useEffect(() => {
    if (allAuctionItems.length === 0) return;

    const uniqueLocations = Array.from(
      new Set(
        allAuctionItems
          .map((item) => item.location)
          .filter((loc) => loc && loc !== ""),
      ),
    );

    const dbLocations = uniqueLocations.map((loc) => ({
      value: loc,
      label: loc.charAt(0).toUpperCase() + loc.slice(1),
    }));

    setLocations([{ value: "all", label: "Location" }, ...dbLocations]);
  }, [allAuctionItems]);

  useEffect(() => {
    const fetchAuctions = async () => {
      try {
        setIsLoading(true);
        const params = new URLSearchParams();
        params.set("saleTypes", "1");
        params.set("view", "listing");
        if (dbCategoryHandle && dbCategoryHandle !== "all") {
          params.set("category", dbCategoryHandle);
        }
        if (effectiveMode === "forward" || effectiveMode === "reverse") {
          params.set("auctionType", effectiveMode);
        }

        const res = await fetch(`/api/auctions?${params.toString()}`);
        const json = await res.json();
        // console.log("Fetched auctions:", json.data); // Debug log
        if (!json.success) return;
        const mapped: AuctionItem[] = (json.data || []).map((a: any) => {
          // Calculate start and end times
          const start = a.scheduledstart ? new Date(a.scheduledstart) : null;
          const duration = a.auctionduration
            ? ((durationObj) =>
                (durationObj.days || 0) * 24 * 60 * 60 +
                (durationObj.hours || 0) * 60 * 60 +
                (durationObj.minutes || 0) * 60)(a.auctionduration)
            : 0;
          const end = start
            ? new Date(start.getTime() + duration * 1000)
            : null;
          const now = new Date();

          let status: "live" | "upcoming" | "closed" = "upcoming";
          if (a.ended) {
            // If ended flag in DB is true, mark closed
            status = "closed";
          } else if (start && end) {
            if (now < start) status = "upcoming";
            else if (now >= start && now < end) status = "live";
            else if (now >= end) status = "closed";
          }

          /////////////////////////////////////////////////////////

          // Debug log for category structure
          // console.log("Auction category data:", a.id, a.categories);

          const categoryHandle = Array.isArray(a.categories)
            ? a.categories[0]?.handle
            : a.categories?.handle;

          return {
            id: a.id,
            title: a.productname || a.title || "Untitled Auction",
            category: categoryHandle || "null",
            image:
              Array.isArray(a.productimages) && a.productimages.length > 0
                ? a.productimages[0] // Use first URL from productimages
                : "/placeholder.svg",
            auctiontype: a.auctiontype,
            status,
            buy_now_price: a.buy_now_price,
            // location: a.profiles?.location || "",
            scheduledstart: a.scheduledstart || "",
            auctionduration: a.auctionduration || "",
            featured: a.is_featured || false,
            verified: a.verified || false,
            bidincrementtype: a.bidincrementtype,
            percent: a.percent,
            currentBid: a.currentbid ?? undefined,
            timeLeft: end && status === "live" ? end.toISOString() : "",
            bidders: a.bidcount ?? undefined,
            // seller: a.createdby || "",
            seller: a.seller || "", // This is now a UUID
            fname: a.profiles?.fname || "",
            role: a.profiles?.role || "",
            rating: a.rating ?? undefined,
            targetPrice: a.targetprice ?? undefined,
            deadline: "", // You can calculate this if you have end time
            proposals: a.proposals ?? undefined,
            buyer: a.buyer || "",
            startingBid: a.startprice ?? undefined,
            startsIn: start && status === "upcoming" ? start.toISOString() : "",
            finalBid: a.finalbid ?? undefined,
            endedAgo: "", // You can calculate this if you have end time
            winner: a.winner || "",
            views: a.views,
            bidder_count: a.bidder_count,
            minimumincrement: a.minimumincrement,
            currency: a.currency,
            watchers: a.watchers ?? undefined,
            productimages: a.productimages || [], // Array of Supabase Storage URLs
            productdocuments: a.productdocuments || [], // Array of Supabase Storage URLs
            createdat: a.createdat || "", // For sorting if needed
            auctionsubtype: a.auctionsubtype || undefined, // Map auctionsubtype
            location: a.profiles?.location || a.location || "",
            // fname: a.profiles?.fname || "", // add this
            product_heromsg: a.product_heromsg || "",
            sub_handle: a.sub_handle || "",
          };
        });
        setAllAuctionItems(mapped);
      } catch (error) {
        console.error("Failed to fetch auctions:", error);
      } finally {
        setIsLoading(false);
      }
    };
    fetchAuctions();
  }, [dbCategoryHandle, effectiveMode]);

  const filterAndSortAuctions = (
    status: "live" | "upcoming" | "closed",
    auctiontype?: "forward" | "reverse",
  ) => {
    let items = allAuctionItems.filter((item) => {
      return (
        item.status === status &&
        (auctiontype ? item.auctiontype === auctiontype : true)
      );
    });
    if (excludeId) {
      items = items.filter((item) => item.id !== excludeId); // exclude current auction
    }
    if (searchTerm) {
      items = items.filter((item) =>
        item.title.toLowerCase().includes(searchTerm.toLowerCase()),
      );
    }
    if (selectedCategory !== "all") {
      items = items.filter(
        (item) =>
          item.category?.toLowerCase() === selectedCategory?.toLowerCase(),
      );
    }
    if (selectedLocation !== "all") {
      items = items.filter((item) => item.location === selectedLocation);
    }
    if (!auctiontype && selectedauctiontype !== "all") {
      items = items.filter((item) => item.auctiontype === selectedauctiontype);
    }
    if (selectedSubtype !== "all") {
      items = items.filter((item) => item.auctionsubtype === selectedSubtype);
    }

    // Respect domain mode even for logged-out visitors.
    if (effectiveMode === "forward") {
      items = items.filter((item) => item.auctiontype === "forward");
    } else if (effectiveMode === "reverse") {
      items = items.filter((item) => item.auctiontype === "reverse");
    }

    // Sorting logic
    if (status === "live") {
      if (sortBy === "ending-soon") {
        items.sort((a, b) =>
          a.timeLeft && b.timeLeft ? a.timeLeft.localeCompare(b.timeLeft) : 0,
        );
      } else if (sortBy === "price-high") {
        items.sort(
          (a, b) =>
            (b.currentBid || b.targetPrice || 0) -
            (a.currentBid || a.targetPrice || 0),
        );
      } else if (sortBy === "price-low") {
        items.sort(
          (a, b) =>
            (a.currentBid || a.targetPrice || 0) -
            (b.currentBid || b.targetPrice || 0),
        );
      } else if (sortBy === "most-bids") {
        items.sort(
          (a, b) =>
            (b.bidders || b.proposals || 0) - (a.bidders || a.proposals || 0),
        );
      } else if (sortBy === "most-watched") {
        items.sort((a, b) => (b.watchers || 0) - (a.watchers || 0));
      } else if (sortBy === "newest") {
        items.sort((a, b) =>
          a.createdat && b.createdat
            ? b.createdat.localeCompare(a.createdat)
            : 0,
        );
      }
    }

    return items;
  };

  const liveAuctions = useMemo(
    () => filterAndSortAuctions("live"),
    [
      searchTerm,
      selectedCategory,
      selectedLocation,
      selectedauctiontype,
      selectedSubtype,
      sortBy,
      allAuctionItems,
    ],
  );

  const upcomingAuctions = useMemo(
    () => filterAndSortAuctions("upcoming"),
    [
      searchTerm,
      selectedCategory,
      selectedLocation,
      selectedauctiontype,
      selectedSubtype,
      sortBy,
      allAuctionItems,
    ],
  );
  const liveForwardAuctions = useMemo(
    () => filterAndSortAuctions("live", "forward"),
    [
      searchTerm,
      selectedCategory,
      selectedLocation,
      selectedauctiontype,
      selectedSubtype,
      sortBy,
      allAuctionItems,
    ],
  );
  const liveReverseAuctions = useMemo(
    () => filterAndSortAuctions("live", "reverse"),
    [
      searchTerm,
      selectedCategory,
      selectedLocation,
      // selectedauctiontype,
      selectedSubtype,
      sortBy,
      allAuctionItems,
    ],
  );
  const upcomingForwardAuctions = useMemo(
    () => filterAndSortAuctions("upcoming", "forward"),
    [
      searchTerm,
      selectedCategory,
      selectedLocation,
      selectedauctiontype,
      selectedSubtype,
      sortBy,
      allAuctionItems,
    ],
  );
  const upcomingReverseAuctions = useMemo(
    () => filterAndSortAuctions("upcoming", "reverse"),
    [
      searchTerm,
      selectedCategory,
      selectedLocation,
      selectedSubtype,
      // selectedauctiontype,
      sortBy,
      allAuctionItems,
    ],
  );

  const closedAuctions = useMemo(
    () => filterAndSortAuctions("closed"),
    [
      searchTerm,
      selectedCategory,
      selectedLocation,
      selectedauctiontype,
      selectedSubtype,
      sortBy,
      allAuctionItems,
    ],
  );
  interface Props {
    auction: AuctionItem;
  }

  const handleLoadMore = (tab: "live" | "upcoming" | "closed") => {
    if (tab === "live") {
      setVisibleLive((prev) => {
        const next = prev + 8;
        return next >= liveAuctions.length ? liveAuctions.length : next;
      });
    }
    if (tab === "upcoming") {
      setVisibleUpcoming((prev) => {
        const next = prev + 8;
        return next >= upcomingAuctions.length ? upcomingAuctions.length : next;
      });
    }
    if (tab === "closed") {
      setVisibleClosed((prev) => {
        const next = prev + 8;
        return next >= closedAuctions.length ? closedAuctions.length : next;
      });
    }
  };
  return (
    // <div className="min-h-screen py-20 bg-gradient-to-br from-gray-50 to-gray-100">
    <>
      {isLoading ? (
        <div />
      ) : (
        <div
          className={`${
            customHide ? "py-20" : "pt-6 pb-10"
          } min-h-screen bg-gradient-to-b from-[#FAFAF9] via-[#F6F6F4] to-[#EFEFEA]`}
        >
          <div className="w-full max-w-[1390px] mx-auto px-4">
            {/* <div className="container mx-auto px-4"> */}
            {/* Hero Section */}
            {/* Hero Section - Generic Hidden on Category Page */}
            {false && (
              <div>
                <div className="text-center mb-12">
                  <h1 className="text-4xl md:text-6xl font-bold mb-6 bg-gradient-to-r from-gray-900 to-gray-600 bg-clip-text text-transparent">
                    Discover Amazing Deals
                  </h1>
                  <p className="text-xl text-gray-600 max-w-3xl mx-auto mb-8 dark:text-[#ffff]">
                    Join thousands of buyers and sellers in our dynamic marketplace.
                    Find unique items, great deals, and business opportunities.
                  </p>

                  {/* Quick Stats */}
                  <div className="flex flex-wrap justify-center gap-4 max-w-4xl mx-auto mb-8">
                    {effectiveMode !== "reverse" && (
                      <div
                        onClick={() => {
                          setTab("live");
                          setSelectedauctiontype("forward");
                        }}
                        className={`text-center p-4 rounded-2xl transition-all duration-300 cursor-pointer overflow-hidden break-words shadow-sm w-[calc(50%-1rem)] md:w-48
               ${
                 tab === "live" && selectedauctiontype === "forward"
                   ? "bg-blue-100 shadow-md ring-2 ring-blue-300"
                   : "bg-white"
               }
               hover:bg-blue-200 hover:shadow-lg`}
                      >
                        <div className="text-xl font-bold text-orange-600 flex items-center justify-center gap-1">
                          <TrendingUp className="h-4 w-4 text-gray-600" />
                          {liveForwardAuctions.length}
                        </div>
                        <div className="text-sm text-gray-700 mt-1">
                          Live Forward Auctions
                        </div>
                      </div>
                    )}

                    {effectiveMode !== "forward" && (
                      <div
                        onClick={() => {
                          setTab("live"); // Switch to live tab
                          setSelectedauctiontype("reverse"); // Set filter
                        }}
                        className={`text-center p-4 rounded-2xl transition-all duration-300 cursor-pointer overflow-hidden break-words shadow-sm w-[calc(50%-1rem)] md:w-48 ${
                          tab === "live" && selectedauctiontype === "reverse"
                            ? "bg-blue-100 shadow-md ring-2 ring-blue-300"
                            : "bg-white"
                        }  hover:bg-blue-200 hover:shadow-lg`}
                      >
                        <div className="text-xl font-bold text-teal-600 flex items-center justify-center gap-1">
                          <TrendingDown className="h-4 w-4 text-gray-600" />
                          {liveReverseAuctions.length}
                        </div>
                        <div className="text-sm text-gray-600">
                          Live Reverse Auctions
                        </div>
                      </div>
                    )}
                    {effectiveMode !== "reverse" && (
                      <div
                        onClick={() => {
                          setTab("upcoming"); // Switch to upcoming tab
                          setSelectedauctiontype("forward"); // Set filter
                        }}
                        className={`text-center p-4 rounded-2xl transition-all duration-300 cursor-pointer overflow-hidden break-words shadow-sm w-[calc(50%-1rem)] md:w-48 ${
                          tab === "upcoming" && selectedauctiontype === "forward"
                            ? "bg-blue-100 shadow-md ring-2 ring-blue-300"
                            : "bg-white"
                        } hover:bg-blue-200 hover:shadow-lg`}
                      >
                        <div className="text-xl font-bold text-orange-600 flex items-center justify-center gap-1">
                          <TrendingUp className="h-4 w-4 text-gray-600" />
                          {upcomingForwardAuctions.length}
                        </div>
                        <div className="text-sm text-gray-600">
                          Upcoming Forward Auctions
                        </div>
                      </div>
                    )}
                    {effectiveMode !== "forward" && (
                      <div
                        onClick={() => {
                          setTab("upcoming"); // Switch to "Starting Soon" tab
                          setSelectedauctiontype("reverse"); // Set filter to reverse
                        }}
                        className={`text-center p-4 rounded-2xl transition-all duration-300 cursor-pointer overflow-hidden break-words shadow-sm w-[calc(50%-1rem)] md:w-48  ${
                          tab === "upcoming" && selectedauctiontype === "reverse"
                            ? "bg-blue-100 shadow-md ring-2 ring-blue-300"
                            : "bg-white"
                        } hover:bg-blue-200 hover:shadow-lg`}
                      > 
                        <div className="text-xl font-bold text-teal-600 flex items-center justify-center gap-1">
                          <TrendingDown className="h-4 w-4 text-gray-600" />
                          {upcomingReverseAuctions.length}
                        </div>
                        <div className="text-sm text-gray-600">
                          Upcoming Reverse Auctions
                        </div>
                      </div>
                    )}
                  </div>
                </div>
                {/* Search and Filters */}
                <Card className="mb-8 shadow-lg border border-gray-200 bg-white overflow-x-hidden ">
                  <CardContent className="p-4 ">
                    <div className="flex flex-col lg:flex-row gap-4 items-center overflow-x-hidden">
                      {/* Search and Filters */}
                      <div className="flex-1 flex flex-wrap gap-2 items-center overflow-x-hidden">
                        {/* Search Input */}
                        <div className="relative flex-grow min-w-[200px]">
                          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
                          <Input
                            placeholder="Search auctions, brands, categories..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            className="pl-10 h-12 border border-gray-300 w-full"
                          />
                        </div>
                        <div className="flex flex-wrap gap-2">
                          {/* Location Filter */}
                          <div className="flex gap-2 flex-wrap">
                            <Select
                              value={selectedauctiontype}
                              onValueChange={setSelectedauctiontype}
                            >
                              <SelectTrigger className="w-40 h-12 max-w-full">
                                <SelectValue placeholder="Type" />
                              </SelectTrigger>
                              <SelectContent className="z-50 text-left">
                                {auctiontypes.map((type) => (
                                  <SelectItem key={type.value} value={type.value}>
                                    {type.label}
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                            <Select
                              value={selectedLocation}
                              onValueChange={setSelectedLocation}
                            >
                              <SelectTrigger className="w-40 h-12 max-w-full">
                                <SelectValue placeholder="All Locations" />
                              </SelectTrigger>
                              <SelectContent className="z-50">
                                {locationsCount.map((location) => (
                                  <SelectItem
                                    key={location.value}
                                    value={location.value}
                                  >
                                    {location.label}
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                            <Select
                              value={selectedCategory}
                              onValueChange={setSelectedCategory}
                            >
                              <SelectTrigger className="w-40 h-12 max-w-full">
                                <SelectValue placeholder="Category" />
                              </SelectTrigger>
                              <SelectContent className="z-50">
                                {mergedCategories.map((category) => (
                                  <SelectItem
                                    key={category.value}
                                    value={category.value}
                                  >
                                    {category.label}
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          </div>
                        </div>
                        <Select
                          value={selectedSubtype}
                          onValueChange={setSelectedSubtype}
                        >
                          <SelectTrigger className="w-40 h-12 max-w-full">
                            <SelectValue placeholder="Auction style" />
                          </SelectTrigger>
                          <SelectContent className="z-50 text-left">
                            {subtypesWithCounts.map((subtype) => (
                              <SelectItem
                                key={subtype.value}
                                value={subtype.value}
                                className="pl-8 pr-2 text-sm"
                              >
                                {subtype.label}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>

                        <Select>
                          <SelectTrigger className="w-40 h-12 max-w-full">
                            <SelectValue placeholder="Price" />
                          </SelectTrigger>
                          <SelectContent className="z-50">
                            <SelectItem value="ending-soon">Ending Soon</SelectItem>
                            <SelectItem value="price-high">
                              Price: High to Low
                            </SelectItem>
                            <SelectItem value="price-low">
                              Price: Low to High
                            </SelectItem>
                            <SelectItem value="most-bids">Most Bids</SelectItem>
                            <SelectItem value="most-watched">
                              Most Watched
                            </SelectItem>
                            <SelectItem value="newest">Newest First</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </div>
            )}
            {/* Category Page Content */}
            {/* Banner */}
            {/* Auto-Sliding Banner Carousel */}
            <CategoryBannerCarousel
              auctions={allAuctionItems.filter(
                (item) =>
                  item.featured &&
                  (dbCategoryHandle === "all" ||
                    item.category?.toLowerCase() ===
                      dbCategoryHandle?.toLowerCase()),
              )}
            />
            {/* Existing Products List and Tabs */}
            {heading && (
              <div className="mb-8">
                <h2 className="text-3xl font-bold mb-4 text-gray-900">
                  Explore Auctions
                </h2>
              </div>
            )}
            {/* Auction Tabs */}
            {customHide ? (
              <Tabs
                value={tab}
                onValueChange={(value) => {
                  if (
                    value === "live" ||
                    value === "upcoming" ||
                    value === "closed"
                  ) {
                    setTab(value);
                  }
                }}
                className="w-full"
              >
                <TabsList className="grid w-full grid-cols-3 bg-white shadow-sm h-12 border border-gray-200">
                  <TabsTrigger
                    value="live"
                    className="flex items-center justify-center gap-2 data-[state=active]:bg-green-50 data-[state=active]:text-green-700"
                  >
                    <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></div>
                    Live Auctions ({liveAuctions.length})
                  </TabsTrigger>
                  <TabsTrigger
                    value="upcoming"
                    ref={upcomingTabRef} // <-- Attach ref here
                    className="flex items-center justify-center gap-2 data-[state=active]:bg-blue-50 data-[state=active]:text-blue-700"
                  >
                    <Calendar className="h-4 w-4" />
                    Upcoming Auctions ({upcomingAuctions.length})
                  </TabsTrigger>
                  <TabsTrigger
                    value="closed"
                    className="flex items-center justify-center gap-2 data-[state=active]:bg-gray-50 data-[state=active]:text-gray-700"
                  >
                    <CheckCircle className="h-4 w-4" />
                    Recently Closed ({closedAuctions.length})
                  </TabsTrigger>
                </TabsList>

                <TabsContent value="live" className="mt-8">
                  {liveAuctions.length > 0 ? (
                    <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
                      {liveAuctions.slice(0, visibleLive).map((auction) => (
                        <AuctionCard
                          key={auction.id}
                          auction={auction}
                          onCategoryClick={(cat) => setSelectedCategory(cat)} // ✅ Pass this prop
                        />
                      ))}
                    </div>
                  ) : (
                    <div className="text-center py-12">
                      <div className="text-gray-400 mb-4">
                        <TrendingUp className="h-12 w-12 mx-auto" />
                      </div>
                      <h3 className="text-lg font-semibold text-gray-600 mb-2">
                        No live auctions found
                      </h3>
                      <p className="text-gray-500">
                        Try adjusting your filters or check back later.
                      </p>
                    </div>
                  )}
                  {visibleLive < liveAuctions.length && (
                    <div className="text-center mt-12">
                      <Button
                        variant="outline"
                        size="lg"
                        className="px-8 border-2 border-gray-300 text-gray-700 hover:bg-gray-100 hover:border-gray-400"
                        onClick={() => handleLoadMore("live")}
                      >
                        Load More Auctions
                      </Button>
                    </div>
                  )}
                </TabsContent>

                <TabsContent value="upcoming" className="mt-8">
                  {upcomingAuctions.length > 0 ? (
                    <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
                      {upcomingAuctions.slice(0, visibleUpcoming).map((auction) => (
                        <AuctionCard
                          key={auction.id}
                          auction={auction}
                          onCategoryClick={(cat) => setSelectedCategory(cat)} // ✅ ADD THIS
                        />
                      ))}
                    </div>
                  ) : (
                    <div className="text-center py-12">
                      <div className="text-gray-400 mb-4">
                        <Calendar className="h-12 w-12 mx-auto" />
                      </div>
                      <h3 className="text-lg font-semibold text-gray-600 mb-2">
                        No upcoming auctions found
                      </h3>
                      <p className="text-gray-500">
                        Try adjusting your filters or check back later.
                      </p>
                    </div>
                  )}
                  {visibleUpcoming < upcomingAuctions.length && (
                    <div className="text-center mt-12">
                      <Button
                        variant="outline"
                        size="lg"
                        className="px-8 border-2 border-gray-300 text-gray-700 hover:bg-gray-100 hover:border-gray-400"
                        onClick={() => handleLoadMore("upcoming")}
                      >
                        Load More Auctions
                      </Button>
                    </div>
                  )}
                </TabsContent>

                <TabsContent value="closed" className="mt-8">
                  {closedAuctions.length > 0 ? (
                    <div
                      className={`grid gap-6 ${"md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4"}`}
                    >
                      {closedAuctions.slice(0, visibleClosed).map((auction) => (
                        <AuctionCard
                          key={auction.id}
                          auction={auction}
                          onCategoryClick={(cat) => setSelectedCategory(cat)} // ✅ Add this line
                        />
                      ))}
                    </div>
                  ) : (
                    <div className="text-center py-12">
                      <div className="text-gray-400 mb-4">
                        <CheckCircle className="h-12 w-12 mx-auto" />
                      </div>
                      <h3 className="text-lg font-semibold text-gray-600 mb-2">
                        No closed auctions found
                      </h3>
                      <p className="text-gray-500">
                        Try adjusting your filters or check back later.
                      </p>
                    </div>
                  )}
                  {visibleClosed < closedAuctions.length && (
                    <div className="text-center mt-12">
                      <Button
                        variant="outline"
                        size="lg"
                        className="px-8 border-2 border-gray-300 text-gray-700 hover:bg-gray-100 hover:border-gray-400"
                        onClick={() => handleLoadMore("closed")}
                      >
                        Load More Auctions
                      </Button>
                    </div>
                  )}
                </TabsContent>
              </Tabs>
            ) : (
              <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
                {liveAuctions.slice(0, visibleLive).map((auction) => (
                  <AuctionCard
                    key={auction.id}
                    auction={auction}
                    onCategoryClick={(cat) => setSelectedCategory(cat)}
                  />
                ))}
              </div>
            )}
                {/* Subcategories */}
            <div className="mb-6 pt-10">
              <h2 className="text-3xl font-bold text-gray-900">Related Subcategories</h2>
            </div>
            <SubCategoryList categoryHandle={dbCategoryHandle} />
          </div>
          
        </div>
      )}
    </>
    // </div>
  );
}
